import { employeeManagementServiceApi } from "../../../app/superAdmin/employeeManagementServiceApi";
import { API_URL } from "../../../constants/apiUrl";
import {
  GetAllCompanyResponseType,
  GetAllDepartmentParamsType,
  GetAllDepartmentResponseType,
  GetAllEmployeesParamsType,
  GetAllEmployeesResponseType,
  GetAllSubDepartmentParamsType,
  GetAllSubDepartmentResponseType,
  GetEmployeeDetailsTypes,
  GetEmployeeResponseType,
  GetQueryArgsType,
  GetUserListResponseType,
} from "../../../types";
import { GetRolesResponse } from "../../../types/role";

export const employeeManagementApi =
  employeeManagementServiceApi.injectEndpoints({
    endpoints: (builder) => ({
      getRoleList: builder.query<GetRolesResponse, unknown>({
        query: () => ({
          url: API_URL.getRoleList,
        }),
      }),
      updateEmployee: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateEmployee}/${id}`,
          method: "PATCH",
          body: data,
        }),
      }),
      fileUpload: builder.mutation({
        query: (data) => ({
          url: API_URL.fileUpload,
          method: "PATCH",
          body: data,
        }),
      }),

      createDepartment: builder.mutation({
        query: (data) => ({
          url: API_URL.createDepartment,
          method: "POST",
          body: data,
        }),
        invalidatesTags: (result) => [{ type: "GetAllDepartment" }],
      }),

      updateDepartment: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateDepartment}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (result) => [
          { type: "GetAllDepartment", id: result?.data?.id },
          { type: "GetAllDepartment" },
        ],
      }),

      statusUpdate: builder.mutation({
        query: ({ data, id }) => ({
          url: `${API_URL.statusUpdate}/${id}/status`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (result) => [
          { type: "GetAllDepartment", id: result?.data?.id },
          { type: "GetAllDepartment" },
        ],
      }),

      getAllDepartment: builder.query<
        GetAllDepartmentResponseType,
        GetQueryArgsType<GetAllDepartmentParamsType>
      >({
        query: (queryArgs) => ({
          url: API_URL.getAllDepartment,
          ...queryArgs,
        }),
        providesTags: ["GetAllDepartment"],
      }),

      getDepartmentById: builder.query({
        query: (id) => ({
          url: `${API_URL.getDepartmentById}/${id}`,
        }),
        providesTags: (_, __, id) => [{ type: "GetAllDepartment", id }],
      }),

      createSubDepartment: builder.mutation({
        query: (data) => ({
          url: API_URL.createSubDepartment,
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["GetSubAllDepartment"],
      }),

      updateSubDepartment: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateSubDepartment}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (_, __, id) => [
          { type: "GetSubAllDepartment", id },
          { type: "GetSubAllDepartment" },
        ],
      }),

      updateSubDepartmentStatus: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateSubDepartmentStatus}/${id}/status`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (_, __, id) => [
          { type: "GetSubAllDepartment", id },
          { type: "GetSubAllDepartment" },
        ],
      }),

      getAllSubDepartment: builder.query<
        GetAllSubDepartmentResponseType,
        GetQueryArgsType<GetAllSubDepartmentParamsType>
      >({
        query: (queryArgs) => ({
          url: API_URL.getAllSubDepartment,
          ...queryArgs,
        }),
        providesTags: ["GetSubAllDepartment"],
      }),

      getSubDepartmentById: builder.mutation({
        query: (id) => ({
          url: `${API_URL.getSubDepartmentById}/${id}`,
        }),
      }),

      getSubDepartmentByIdGroupChat: builder.query<
        GetAllSubDepartmentResponseType,
        Number
      >({
        query: (id) => {
          return {
            url: `${API_URL.getSubDepartmentByIdGroupChat}/${id}`,
          };
        },
      }),

      createEmployee: builder.mutation({
        query: (data) => ({
          url: API_URL.createEmployee,
          method: "POST",
          body: data,
        }),
        invalidatesTags: (result) => [
          { type: "GetAllEmployees" },
          { type: "GetAllEmployeesDetails", id: result?.data?.id },
        ],
      }),

      updateEmployeeById: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateEmployeeById}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (_, __, id) => {
          // console.log("Invalidating tags", id);
          return [
            { type: "GetAllEmployeesDetails", id },
            { type: "GetAllEmployees" },

            "GetEmployeesProfile",
          ];
        },
      }),

      getAllEmployees: builder.query<
        GetAllEmployeesResponseType,
        GetQueryArgsType<GetAllEmployeesParamsType>
      >({
        query: (queryArgs) => ({
          url: API_URL.getAllEmployees,
          ...queryArgs,
        }),
        providesTags: ["GetAllEmployees"],
      }),

      getEmployeeDetails: builder.query<GetEmployeeDetailsTypes, string>({
        query: (id) => ({
          url: `${API_URL.getEmployeeDetails}/${id}`,
        }),
        providesTags: (_, __, id) => [{ type: "GetAllEmployeesDetails", id }],
      }),

      getMe: builder.query<GetEmployeeResponseType, string>({
        query: (id) => ({
          url: `${API_URL.profile}/${id}`,
        }),
        providesTags: ["GetEmployeesProfile"],
      }),

      updateEmployeeStatus: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.updateEmployeeStatus}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (_, __, id) => [
          { type: "GetAllEmployeesDetails", id },
          { type: "GetAllEmployees" },
          { type: "GetAllEmployeesDetails" },
          "GetEmployeesProfile",
        ],
      }),

      uploadProfilePicture: builder.mutation({
        query: ({ id, data }) => ({
          url: `${API_URL.uploadProfilePicture}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: (_, __, id) => [
          { type: "GetAllEmployeesDetails", id },
          { type: "GetAllEmployees" },
        ],
      }),

      createRole: builder.mutation({
        query: (data) => ({
          url: API_URL.createRole,
          method: "POST",
          body: data,
        }),
      }),

      getEmployeeBasedOnRole: builder.query({
        query: (id) => ({
          url: `${API_URL.getEmployeeBasedOnRole}/${id}`,
        }),
      }),
      getEmployeeList: builder.query<
        GetUserListResponseType,
        GetQueryArgsType<{
          search?: string;
          department_id: string | number;
          sub_department_id?: string | number;
        }>
      >({
        query: (queryArgs) => ({
          url: API_URL.getUserList,
          ...queryArgs,
        }),
      }),

      getAllCompanyForDepartment: builder.query<
        GetAllCompanyResponseType,
        void
      >({
        query: () => ({
          url: `${API_URL.getAllCompanyForDepartment}`,
        }),
      }),
      getCompanyProfile: builder.query({
        query: () => ({
          url: `/employees/v1/get-emp-company`,
          method: "GET",
        }),
      }),

      giveReportDownloadAccess: builder.mutation({
        query: ({ data, id }) => ({
          url: `${API_URL.giveReportDownloadPermission}/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: ["GetAllEmployees"],
      }),
    }),
  });

export const {
  useGetCompanyProfileQuery,
  useGetRoleListQuery,
  useUpdateEmployeeMutation,
  useFileUploadMutation,
  useGetAllEmployeesQuery,
  useGetAllDepartmentQuery,
  useGetEmployeeBasedOnRoleQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeByIdMutation,
  useUpdateEmployeeStatusMutation,
  useGetEmployeeDetailsQuery,
  useGetAllSubDepartmentQuery,
  useGetSubDepartmentByIdGroupChatQuery,
  useCreateSubDepartmentMutation,
  useUpdateSubDepartmentMutation,
  useGetAllCompanyForDepartmentQuery,
  useGetEmployeeListQuery,
  // Department
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useUpdateSubDepartmentStatusMutation,
  useStatusUpdateMutation,
  useGetMeQuery,
  useGiveReportDownloadAccessMutation,
} = employeeManagementApi;
